import { ReactNode, RefObject, useState } from 'react';

import { ChevronDownIcon, LockIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { focusVisibleRing, invalidRing } from '@/components/ui/constants';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { OptionBase } from '@/shared/types/common';
import {
  CONVERSATION_SCOPE_LABEL_KEYS,
  CONVERSATION_SCOPES,
  ConversationScope,
  ENVIRONMENT_SCOPE_LABEL_KEYS,
  ENVIRONMENT_SCOPES,
  EnvironmentScope,
  groupLevels,
  levelAtLeast,
  levelHasExtras,
  PERMISSION_LEVEL_LABEL_KEYS,
  PermissionGroup,
  PermissionLevel,
  PermissionToggle,
} from '@/util/permissionCatalog';

const ToggleRow = ({
  groupKey,
  toggle,
  checked,
  disabled,
  onChange,
}: {
  groupKey: string;
  toggle: Pick<PermissionToggle, 'key' | 'labelKey' | 'descriptionKey'>;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) => {
  const { t } = useTranslation();
  const id = `${groupKey}-${toggle.key}`;
  const descriptionId = `${id}-description`;

  return (
    <div className="flex items-start gap-2 self-stretch">
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        aria-describedby={descriptionId}
        onCheckedChange={(next) => onChange(next === true)}
      />
      <div className="flex flex-1 flex-col items-start gap-1">
        <Label
          htmlFor={id}
          className="self-stretch text-sm leading-none font-medium text-foreground"
        >
          {t(toggle.labelKey)}
        </Label>
        <span
          id={descriptionId}
          className="self-stretch text-sm leading-5 font-normal text-foreground-muted"
        >
          {t(toggle.descriptionKey)}
        </span>
      </div>
    </div>
  );
};

const ScopeField = ({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: (labelId: string) => ReactNode;
}) => {
  const labelId = `${id}-scope-label`;

  return (
    <div className="flex min-w-32 flex-col items-start gap-3 self-stretch rounded-md">
      <div className="flex w-full max-w-113 min-w-32 items-center gap-2 pr-3">
        <LockIcon size={16} className="shrink-0 text-foreground-muted" />
        <span
          id={labelId}
          className="line-clamp-1 overflow-hidden text-sm leading-none font-medium text-ellipsis text-foreground"
        >
          {label}
        </span>
      </div>
      {children(labelId)}
    </div>
  );
};

const EnvironmentPicker = ({
  labelledBy,
  options,
  selected,
  disabled,
  error,
  triggerRef,
  onChange,
}: {
  labelledBy: string;
  options: OptionBase[];
  selected: string[];
  disabled?: boolean;
  error?: string;
  triggerRef?: RefObject<HTMLButtonElement>;
  onChange: (ids: string[]) => void;
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const isPicked = new Set(selected);
  const labelById = new Map(options.map((option) => [option.value, option.label]));
  const pickedLabels = selected.map((id) => labelById.get(id) ?? id);
  const valueId = `${labelledBy}-value`;
  const errorId = `${labelledBy}-error`;

  const toggle = (value: string) =>
    onChange(isPicked.has(value) ? selected.filter((id) => id !== value) : [...selected, value]);

  return (
    <div className="flex w-full flex-col gap-1.5">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            aria-labelledby={`${labelledBy} ${valueId}`}
            aria-describedby={error ? errorId : undefined}
            disabled={disabled}
            className={cn(
              'flex w-full max-w-80 items-center gap-4 rounded-lg border border-stroke-input bg-background px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50',
              focusVisibleRing,
              error && invalidRing
            )}
          >
            <span
              id={valueId}
              className="line-clamp-1 flex-1 overflow-hidden text-left text-sm leading-5 font-normal text-ellipsis text-foreground"
            >
              {pickedLabels.length > 0
                ? pickedLabels.join(', ')
                : t('roles.scopes.environments_select')}
            </span>
            <ChevronDownIcon className="size-4 shrink-0 text-foreground-muted" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-(--radix-popover-trigger-width) p-0">
          <Command>
            <CommandInput placeholder={t('common.search_dots')} />
            <CommandList>
              <CommandEmpty>
                {options.length === 0
                  ? t('roles.scopes.environments_empty')
                  : t('common.no_results')}
              </CommandEmpty>
              <CommandGroup>
                {options.map((option) => {
                  const isChecked = isPicked.has(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      keywords={[option.label]}
                      className="group min-h-8 cursor-pointer [&_svg]:text-current!"
                      onSelect={() => toggle(option.value)}
                    >
                      <Checkbox
                        checked={isChecked}
                        tabIndex={-1}
                        className={cn(
                          'pointer-events-none transition-opacity',
                          !isChecked &&
                            'opacity-0 group-hover:opacity-100 group-data-[selected=true]:opacity-100'
                        )}
                      />
                      <span className="min-w-0 flex-1 truncate font-medium">{option.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {error && (
        <span id={errorId} className="text-sm text-warning">
          {error}
        </span>
      )}
    </div>
  );
};

const AI_AGENT_CONVERSATIONS_TOGGLE = {
  key: 'ai_agent_conversations',
  labelKey: 'roles.scopes.conversations_ai_agent',
  descriptionKey: 'roles.scopes.conversations_ai_agent_description',
};

const SCOPE_OPTIONS_CLASSES = 'flex flex-col items-start gap-2 self-stretch';

const ScopeRadios = <T extends string>({
  id,
  label,
  scopes,
  labelKeys,
  value,
  disabled,
  onChange,
  children,
}: {
  id: string;
  label: string;
  scopes: readonly T[];
  labelKeys: Record<T, string>;
  value: T;
  disabled?: boolean;
  onChange: (scope: T) => void;
  children?: (labelledBy: string) => ReactNode;
}) => {
  const { t } = useTranslation();

  return (
    <ScopeField id={id} label={label}>
      {(labelledBy) => (
        <>
          <RadioGroup
            value={value}
            disabled={disabled}
            aria-labelledby={labelledBy}
            className={SCOPE_OPTIONS_CLASSES}
            onValueChange={(next) => onChange(next as T)}
          >
            {scopes.map((scope) => (
              <RadioGroupItem key={scope} value={scope} id={`${id}-${scope}`}>
                {t(labelKeys[scope])}
              </RadioGroupItem>
            ))}
          </RadioGroup>
          {children?.(labelledBy)}
        </>
      )}
    </ScopeField>
  );
};

type Props = {
  group: PermissionGroup;
  environmentError?: string;
  environmentTriggerRef?: RefObject<HTMLButtonElement>;
  level: PermissionLevel;
  toggles: Record<string, boolean>;
  conversationScope: ConversationScope;
  aiAgentConversations?: boolean;
  environmentScope: EnvironmentScope;
  environmentIds: string[];
  environmentOptions: OptionBase[];
  disabled?: boolean;
  disabledReason?: string;
  isUserOwner?: boolean;
  onLevelChange: (level: PermissionLevel) => void;
  onToggleChange: (toggleKey: string, checked: boolean) => void;
  onConversationScopeChange: (scope: ConversationScope) => void;
  onAiAgentConversationsChange?: (checked: boolean) => void;
  onEnvironmentScopeChange: (scope: EnvironmentScope) => void;
  onEnvironmentIdsChange: (ids: string[]) => void;
};

export const PermissionGroupRow = ({
  group,
  environmentError,
  environmentTriggerRef,
  level,
  toggles,
  conversationScope,
  aiAgentConversations = false,
  environmentScope,
  environmentIds,
  environmentOptions,
  disabled,
  disabledReason,
  isUserOwner,
  onLevelChange,
  onToggleChange,
  onConversationScopeChange,
  onAiAgentConversationsChange,
  onEnvironmentScopeChange,
  onEnvironmentIdsChange,
}: Props) => {
  const { t } = useTranslation();
  const toggleRows = levelHasExtras(level)
    ? (group.toggles ?? []).filter((toggle) => isUserOwner || !toggle.ownerOnly)
    : [];

  const showEnvironmentScope = group.scope === 'environments' && level !== 'no_access';
  const showConversationScope = group.scope === 'conversations' && levelAtLeast(level, 'read');
  const hasExtras = toggleRows.length > 0 || showEnvironmentScope || showConversationScope;
  const labelId = `${group.key}-label`;

  return (
    <div
      role="group"
      aria-labelledby={labelId}
      className="flex w-full max-w-116 flex-col items-start gap-4"
    >
      <div className="flex min-w-32 items-center gap-2 self-stretch rounded-md border border-stroke bg-background px-3 py-2">
        <span
          id={labelId}
          className="line-clamp-1 flex-1 text-sm leading-5 font-medium text-ellipsis text-foreground"
        >
          {t(group.labelKey)}
        </span>
        {disabled && disabledReason && (
          <span id={`${labelId}-reason`} className="sr-only">
            {disabledReason}
          </span>
        )}
        <Select
          value={level}
          disabled={disabled}
          onValueChange={(value) => onLevelChange(value as PermissionLevel)}
        >
          <InfoTooltip content={disabled ? disabledReason : ''} size="large">
            <div>
              <SelectTrigger
                className="h-auto w-auto min-w-0 justify-center gap-1 rounded-none border-none p-0 leading-none font-medium shadow-none"
                aria-labelledby={labelId}
                aria-describedby={disabled && disabledReason ? `${labelId}-reason` : undefined}
              >
                <SelectValue>{t(PERMISSION_LEVEL_LABEL_KEYS[level])}</SelectValue>
              </SelectTrigger>
            </div>
          </InfoTooltip>
          <SelectContent className="flex w-45 flex-col items-start border-stroke">
            {groupLevels(group).map((option) => (
              <SelectItem key={option} value={option}>
                {t(PERMISSION_LEVEL_LABEL_KEYS[option])}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {hasExtras && (
        <div className="flex flex-col items-start gap-4 self-stretch pl-3">
          {toggleRows.map((toggle) => (
            <ToggleRow
              key={toggle.key}
              groupKey={group.key}
              toggle={toggle}
              checked={!!toggles[toggle.key]}
              disabled={disabled}
              onChange={(checked) => onToggleChange(toggle.key, checked)}
            />
          ))}

          {showEnvironmentScope && (
            <ScopeRadios
              id={group.key}
              label={t('roles.scopes.environments_label')}
              scopes={ENVIRONMENT_SCOPES}
              labelKeys={ENVIRONMENT_SCOPE_LABEL_KEYS}
              value={environmentScope}
              disabled={disabled}
              onChange={onEnvironmentScopeChange}
            >
              {(labelledBy) =>
                environmentScope === 'selected' && (
                  <EnvironmentPicker
                    labelledBy={labelledBy}
                    options={environmentOptions}
                    selected={environmentIds}
                    disabled={disabled}
                    error={environmentError}
                    triggerRef={environmentTriggerRef}
                    onChange={onEnvironmentIdsChange}
                  />
                )
              }
            </ScopeRadios>
          )}

          {showConversationScope && (
            <ScopeRadios
              id={group.key}
              label={t('roles.scopes.conversations_label')}
              scopes={CONVERSATION_SCOPES}
              labelKeys={CONVERSATION_SCOPE_LABEL_KEYS}
              value={conversationScope}
              disabled={disabled}
              onChange={onConversationScopeChange}
            />
          )}

          {showConversationScope && onAiAgentConversationsChange && (
            <ToggleRow
              groupKey={group.key}
              toggle={AI_AGENT_CONVERSATIONS_TOGGLE}
              checked={aiAgentConversations}
              disabled={disabled}
              onChange={onAiAgentConversationsChange}
            />
          )}
        </div>
      )}
    </div>
  );
};
